import { temporal } from 'zundo'
import { create, useStore } from 'zustand'
import type { TemporalState } from 'zundo'
import { getAsset } from '../assets/catalog'
import { BACKGROUND } from '../render/theme'
import {
  createEmptyDocument,
  type OfficeDocument,
  type PlacedItem,
  type RoomConfig,
  type RotationSteps,
  type StyleConfig,
  type WallSide,
} from './document'

const STORAGE_KEY = 'office-render:document:v1'

export interface NewItemInput {
  assetId: string
  x: number
  z: number
  y?: number
  rotation?: RotationSteps
  variant?: string
  wall?: WallSide
}

interface DesignState {
  doc: OfficeDocument
  addItem: (input: NewItemInput) => string
  updateItem: (uid: string, patch: Partial<Omit<PlacedItem, 'uid'>>) => void
  removeItem: (uid: string) => void
  duplicateItem: (uid: string) => string | null
  setRoom: (patch: Partial<RoomConfig>) => void
  setWall: (side: WallSide, style: RoomConfig['walls'][WallSide]) => void
  setStyle: (patch: Partial<StyleConfig>) => void
  rename: (name: string) => void
  clearItems: () => void
  loadDocument: (doc: OfficeDocument) => void
}

function newId(): string {
  return typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `item-${Math.random().toString(36).slice(2, 11)}`
}

export const useDesign = create<DesignState>()(
  temporal(
    (set, get) => ({
      doc: createEmptyDocument(),

      addItem: (input) => {
        const uid = newId()
        const item: PlacedItem = {
          uid,
          assetId: input.assetId,
          x: input.x,
          z: input.z,
          y: input.y ?? 0,
          rotation: input.rotation ?? 0,
          variant: input.variant,
          wall: input.wall,
        }
        set({ doc: { ...get().doc, items: [...get().doc.items, item] } })
        return uid
      },

      updateItem: (uid, patch) => {
        const { doc } = get()
        const items = doc.items.map((item) => (item.uid === uid ? { ...item, ...patch } : item))
        set({ doc: { ...doc, items } })
      },

      removeItem: (uid) => {
        const { doc } = get()
        set({ doc: { ...doc, items: doc.items.filter((item) => item.uid !== uid) } })
      },

      duplicateItem: (uid) => {
        const { doc } = get()
        const source = doc.items.find((item) => item.uid === uid)
        if (!source) return null
        const copy: PlacedItem = { ...source, uid: newId(), x: source.x + 0.25, z: source.z + 0.25 }
        set({ doc: { ...doc, items: [...doc.items, copy] } })
        return copy.uid
      },

      setRoom: (patch) => {
        const { doc } = get()
        set({ doc: { ...doc, room: { ...doc.room, ...patch } } })
      },

      setWall: (side, style) => {
        const { doc } = get()
        set({ doc: { ...doc, room: { ...doc.room, walls: { ...doc.room.walls, [side]: style } } } })
      },

      setStyle: (patch) => {
        const { doc } = get()
        const style = { ...doc.style, ...patch }
        // Theme always owns page colour unless the caller sets background.
        if (patch.theme !== undefined && patch.background === undefined) {
          style.background = BACKGROUND[patch.theme]
        }
        set({ doc: { ...doc, style } })
      },


      rename: (name) => set({ doc: { ...get().doc, name } }),

      clearItems: () => set({ doc: { ...get().doc, items: [] } }),

      loadDocument: (doc) => set({ doc }),
    }),
    {
      // Only the document is historical; everything else in the app is ephemeral.
      partialize: (state) => ({ doc: state.doc }),
      equality: (past, current) => past.doc === current.doc,
      limit: 80,
    },
  ),
)

export const useTemporal = <T,>(selector: (state: TemporalState<{ doc: OfficeDocument }>) => T): T =>
  useStore(useDesign.temporal, selector)

/* ------------------------------------------------------- history gating */

let pauseDepth = 0

/**
 * zundo records every `set()`, so a 60 fps drag would evict the entire history
 * in a second. Gestures wrap their mutations in these; the depth counter makes
 * nesting safe, and callers must always resume in a `finally`.
 */
export function pauseHistory(): void {
  if (pauseDepth === 0) useDesign.temporal.getState().pause()
  pauseDepth += 1
}

export function resumeHistory(): void {
  if (pauseDepth === 0) return
  pauseDepth -= 1
  if (pauseDepth === 0) useDesign.temporal.getState().resume()
}

/** Runs a gesture with history paused, then commits `commit()` as one entry. */
export function withSingleHistoryEntry<T>(run: () => T): T {
  pauseHistory()
  try {
    return run()
  } finally {
    resumeHistory()
  }
}

/* ------------------------------------------------------------ selectors */

export function itemBounds(item: PlacedItem) {
  const asset = getAsset(item.assetId)
  if (!asset) return null
  return { asset, item }
}

/* ---------------------------------------------------------- persistence */

function isDocument(value: unknown): value is OfficeDocument {
  if (!value || typeof value !== 'object') return false
  const candidate = value as Partial<OfficeDocument>
  return (
    candidate.version === 1 &&
    typeof candidate.name === 'string' &&
    Array.isArray(candidate.items) &&
    !!candidate.room &&
    !!candidate.style
  )
}

/** Restores the last session. Called once, before the first paint. */
export function restoreDocument(): void {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    if (!raw) return
    const parsed: unknown = JSON.parse(raw)
    if (!isDocument(parsed)) return
    // Drop items whose asset no longer exists, so a renamed catalog entry cannot
    // wedge the whole document.
    const doc: OfficeDocument = {
      ...createEmptyDocument(),
      ...parsed,
      room: { ...createEmptyDocument().room, ...parsed.room },
      style: { ...createEmptyDocument().style, ...parsed.style },
      items: parsed.items.filter((item) => getAsset(item.assetId)),
    }
    useDesign.getState().loadDocument(doc)
  } catch {
    // A corrupt payload must never block startup.
  } finally {
    // Hydration lands in `pastStates`; without this the first Ctrl+Z wipes the plan.
    useDesign.temporal.getState().clear()
  }
}

let saveTimer: ReturnType<typeof setTimeout> | undefined

export function startAutosave(): () => void {
  return useDesign.subscribe((state) => {
    clearTimeout(saveTimer)
    const snapshot = state.doc
    saveTimer = setTimeout(() => {
      try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(snapshot))
      } catch {
        // Quota errors are not worth interrupting the user over.
      }
    }, 400)
  })
}

export function exportDocumentJson(): string {
  return JSON.stringify(useDesign.getState().doc, null, 2)
}

export function importDocumentJson(json: string): boolean {
  try {
    const parsed: unknown = JSON.parse(json)
    if (!isDocument(parsed)) return false
    useDesign.getState().loadDocument({
      ...createEmptyDocument(),
      ...parsed,
      items: parsed.items.filter((item) => getAsset(item.assetId)),
    })
    return true
  } catch {
    return false
  }
}
